import {SagaIterator} from "redux-saga";
import {all, call, put} from "typed-redux-saga";
import Api, {ApiError} from "modules/utils/api";
import {
	fetchArticlesFailed,
	fetchArticlesSuccess,
	fetchCountriesSuccess,
	fetchFanVoteRankingsFailed,
	fetchFanVoteRankingsSuccess,
	fetchFavVoteSharingImageFailed,
	fetchFavVoteSharingImageSuccess,
	fetchHelpJSONsSuccess,
	fetchPlayersFailed,
	fetchPlayersSuccess,
	fetchSettingsFailed,
	fetchSettingsSuccess,
	fetchSquadsFailed,
	fetchSquadsSuccess,
	toggleErrorModal,
} from "modules/actions";
import {IFetchJSONResponse} from "modules/reducers";
import {ISagaAction, IWithLanguage} from "modules/types";

export const fetchPlayersSaga = function* (): SagaIterator {
	try {
		const players = yield* call(Api.JSON.players);
		ApiError.CHECK_JSON(players, "Error while loading players");
		yield* put(fetchPlayersSuccess(players ?? []));
	} catch (e) {
		yield* put(fetchPlayersFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchSquadsSaga = function* (): SagaIterator {
	try {
		const squads = yield* call(Api.JSON.squads);
		ApiError.CHECK_JSON(squads, "Error while loading Squads");
		yield* put(fetchSquadsSuccess(squads ?? []));
	} catch (e) {
		yield* put(fetchSquadsFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchSettingsSaga = function* (): SagaIterator {
	try {
		const settings = yield* call(Api.JSON.settings);
		ApiError.CHECK_JSON(settings, "Error while loading Settings");
		yield* put(fetchSettingsSuccess(settings ?? {}));
	} catch (e) {
		yield* put(fetchSettingsFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchCountriesSaga = function* (): SagaIterator {
	try {
		const countries = yield* call(Api.JSON.countries);
		ApiError.CHECK_JSON(countries, "Error while loading Countries");
		yield* put(fetchCountriesSuccess(countries ?? []));
	} catch (e) {
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchHelpJSONsSaga = function* () {
	try {
		const categories = yield* call(Api.Help.categories);

		const category = categories.categories.find(
			(category) => category.description === "allstars"
		);

		if (!category) {
			return;
		}

		const [sections, articles] = yield* all([
			call(Api.Help.sections, category.id),
			call(Api.Help.articles, category.id),
		]);
		ApiError.CHECK_JSON(categories, "Error while loading categories");
		ApiError.CHECK_JSON(sections, "Error while loading sections");
		ApiError.CHECK_JSON(articles, "Error while loading articles");
		yield* put(fetchHelpJSONsSuccess({categories, sections, articles} as IFetchJSONResponse));
	} catch (e) {
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchFanVoteRankingsSaga = function* (): SagaIterator {
	try {
		const rankings = yield* call(Api.JSON.fanVoteRankings);
		ApiError.CHECK_JSON(rankings, "Error while loading Rankings");
		yield* put(fetchFanVoteRankingsSuccess(rankings));
	} catch (e) {
		yield* put(fetchFanVoteRankingsFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchArticlesSaga = function* () {
	try {
		const articles = yield* call(Api.JSON.articles);
		ApiError.CHECK_JSON(articles, "Error while loading articles");
		yield* put(fetchArticlesSuccess(articles));
	} catch (e) {
		yield* put(fetchArticlesFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

export const fetchFavVoteSharingImageSaga = function* ({
	payload,
}: ISagaAction<IWithLanguage<number>>): SagaIterator {
	try {
		const response = yield* call(Api.Vote.getShareImage, payload);
		ApiError.CHECK(response);
		yield* put(fetchFavVoteSharingImageSuccess(response.success.image));
	} catch (e) {
		yield* put(fetchFavVoteSharingImageFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};
